import * as React from 'react';
import { AxiosResponse, isAxiosError } from 'axios';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveAccountId';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProjectId';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import FileService, {
  GetFileResponse,
} from '@lifeomic/phc-web-toolkit/src/services/FileService';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import {
  SequenceDetailsBox,
  SequenceDetailsLabel,
  SequenceDetailsValue,
  SequenceDetailsPair,
} from './index';
import { Test } from '../types';

type SpecimenIdBoxProps = {
  test?: Test;
};

const fileService = new FileService();
const fhirService = new FhirService('Observation');

const messages = defineMessages({
  specimenId: {
    id: 'omicsExplorer.specimenId.label',
    defaultMessage: 'Specimen Id:',
  },
  specimenIdTooltip: {
    id: 'omicsExplorer.specimenId.tooltip',
    defaultMessage: 'A unique identifier for the genetic sample',
  },
  unknown: {
    id: 'omicsExplorer.specimenId.unknown',
    defaultMessage: 'Unknown',
  },
});

type SpecimenIdExtractor = (
  data: AxiosResponse<GetFileResponse> | undefined,
) => string | undefined;

// /some/path/to/SPECIMEN-ID.hard-filtered.vcf.gz
const extractBaseFileName: SpecimenIdExtractor = data => {
  return data?.data?.name?.split('/').pop()?.split('.').shift();
};

export const SpecimenIdBox = (props: SpecimenIdBoxProps) => {
  const { formatMessage } = useIntl();
  const query = useSpecimenId(props);

  if (!query.isFetched || query.isLoading) {
    return null;
  }

  return (
    <SequenceDetailsBox>
      <SequenceDetailsPair>
        <SequenceDetailsLabel
          label={formatMessage(messages.specimenId)}
          tooltipTitle={formatMessage(messages.specimenIdTooltip)}
        />
        <SequenceDetailsValue
          preserveCase
          value={query?.data || formatMessage(messages.unknown)}
        />
      </SequenceDetailsPair>
    </SequenceDetailsBox>
  );
};

const retryCondition = (failureCount: number, error: unknown) => {
  return isAxiosError(error)
    ? (error.response?.status ?? 500) >= 500
    : failureCount < 3;
};

const useSpecimenId = ({ test }: SpecimenIdBoxProps) => {
  const auth = useServiceAuth();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();

  const testName = test?.name?.toLowerCase();
  const sourceFileId = test?.sourceFileId ?? '';

  const isNebula = testName === 'nebula';
  const isBroad = testName === 'broad';

  const nebulaQuery = useQuery({
    queryKey: ['specimen-id', 'nebula', sourceFileId],
    queryFn: () =>
      sourceFileId ? fileService.getFile(auth)(sourceFileId) : undefined,
    select: extractBaseFileName,
    enabled: isNebula,
    retry: retryCondition,
  });

  const setTags = test?.sets
    ?.map(set => `http://lifeomic.com/fhir/variant-set-id|${set.id}`)
    .join(',');

  const broadQuery = useQuery({
    queryKey: ['specimen-id', 'broad', setTags, projectId, accountId],
    queryFn: () =>
      setTags
        ? fhirService.search(auth)(accountId, projectId, {
            _tag: [
              'http://lifeomic.com/fhir/survey/generic-default-value|sample-id',
              setTags,
            ],
          })
        : undefined,
    select: response => response?.data?.entry?.[0]?.resource?.valueString,
    retry: retryCondition,
    enabled: isBroad,
  });

  if (isBroad) {
    return broadQuery;
  }

  return nebulaQuery;
};
